type ErrorObject = { [key: string]: any } | any[];

const SENSITIVE_FIELDS = [
    'password',
    'newPassword',
    'currentPassword',
    'confirmPassword',
    'pwd',
    'pass',
    'recaptcha',
    'recaptchaToken',
    'captchaToken',
    'captchaResponse',
    'emailSessionToken'
];

const BODY_FIELD_NAMES = [
    'body',
    'requestBody',
    'payload',
    'data'
];

export function maskSensitiveData(error: ErrorObject): ErrorObject {
    // Handle the case where the top-level error is an array
    if (Array.isArray(error)) {
        return error.map(item => maskSensitiveData(item));
    }

    // Deep clone the error object to avoid mutations
    const maskedError = JSON.parse(JSON.stringify(error));
    
    // Helper function to check if a string might be JSON
    const isPossiblyJSON = (str: string): boolean => {
        return typeof str === 'string' && 
               (str.startsWith('{') || str.startsWith('[')) && 
               (str.endsWith('}') || str.endsWith(']'));
    };

    // Helper function to safely parse JSON
    const safeJSONParse = (str: string): any => {
        try {
            return JSON.parse(str);
        } catch {
            return str;
        }
    };

    // Helper function to find and mask body fields
    const findAndMaskBodyFields = (obj: any): any => {
        if (!obj) return obj;
        
        // Handle primitive types
        if (typeof obj !== 'object') return obj;

        // Handle arrays
        if (Array.isArray(obj)) {
            return obj.map(item => findAndMaskBodyFields(item));
        }

        // Create a new object to store masked data
        const maskedObj = { ...obj };

        for (const key in maskedObj) {
            const value = maskedObj[key];

            // Check if this is a body field that might contain sensitive data
            if (BODY_FIELD_NAMES.some(field => key.toLowerCase().includes(field.toLowerCase()))) {
                if (typeof value === 'string' && isPossiblyJSON(value)) {
                    const parsedValue = safeJSONParse(value);
                    if (parsedValue && typeof parsedValue === 'object') {
                        maskedObj[key] = JSON.stringify(maskSensitiveFields(parsedValue));
                        continue;
                    }
                }
            }

            // Check if the key itself is sensitive
            if (SENSITIVE_FIELDS.some(field => key.toLowerCase().includes(field.toLowerCase()))) {
                maskedObj[key] = '********';
                continue;
            }

            // Recursively process nested objects and arrays
            if (typeof value === 'object' && value !== null) {
                maskedObj[key] = findAndMaskBodyFields(value);
            } else if (typeof value === 'string' && isPossiblyJSON(value)) {
                // Try to parse and mask any JSON strings
                const parsedValue = safeJSONParse(value);
                if (parsedValue && typeof parsedValue === 'object') {
                    maskedObj[key] = JSON.stringify(findAndMaskBodyFields(parsedValue));
                }
            }
        }

        return maskedObj;
    };

    // Helper function to mask sensitive fields in an object
    const maskSensitiveFields = (obj: any): any => {
        if (!obj) return obj;
        
        // Handle primitive types
        if (typeof obj !== 'object') return obj;

        if (Array.isArray(obj)) {
            return obj.map(item => maskSensitiveFields(item));
        }

        const maskedObj = { ...obj };
        for (const key in maskedObj) {
            if (SENSITIVE_FIELDS.some(field => key.toLowerCase().includes(field.toLowerCase()))) {
                maskedObj[key] = '********';
            } else if (typeof maskedObj[key] === 'object' && maskedObj[key] !== null) {
                maskedObj[key] = maskSensitiveFields(maskedObj[key]);
            }
        }
        return maskedObj;
    };

    return findAndMaskBodyFields(maskedError);
}

export interface MaskedLogData {
    value: ErrorObject;
    additionalData?: ErrorObject;
}

export function prepareMaskedLogData(error: ErrorObject, additionalData?: ErrorObject): MaskedLogData {
    const maskedData: MaskedLogData = {
        value: maskSensitiveData(error)
    };

    if (additionalData !== undefined) {
        maskedData.additionalData = maskSensitiveData(additionalData);
    }

    return maskedData;
}